import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import './PatientMetadataEditor.css';
import AppProfileModal from '../AppProfileModal/AppProfileModal';
import { useUser } from '../../../UserContext.js';

const GENDER_OPTIONS = ['MALE', 'FEMALE', 'OTHER'];
const ALCOHOL_OPTIONS = ['NONE', 'SOCIALLY', 'REGULARLY'];

function PatientMetadataEditor({ patient, onClose, onSave }) {
    const { user } = useUser();
    const [isVisible, setIsVisible] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const closeTimeoutRef = useRef(null);
    const [appUsers, setAppUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    const [formData, setFormData] = useState({
        name: patient.name || '',
        family_number: patient.family_number || '',
        date_of_birth: patient.date_of_birth || '',
        gender: patient.gender || '',
        chronic_disease: patient.chronic_disease || '',
        drug_consumption: patient.drug_consumption || '',
        smoking_pcs_per_day: patient.smoking_pcs_per_day || '',
        alcohol_usage: patient.alcohol_usage || '',
        height_cm: patient.height_cm || '',
        weight_kg: patient.weight_kg || '',
        phone: patient.phone || '',
        sample_acceptance_date: patient.sample_acceptance_date || '',
        sample_type: patient.sample_type || '',
        patient_id: patient.patient_id || ''
    });

    useEffect(() => {
        // Show the modal with a slight delay for the animation
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, 50);

        // Fetch app users when the component mounts
        if (formData.patient_id) {
            console.log('Calling fetchAppUsers from useEffect');
            fetchAppUsers();
        }

        return () => {
            clearTimeout(timer);
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, [formData.patient_id]);

    const handleClose = () => {
        setIsClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            onClose();
        }, 200); // Match this with the CSS transition duration
    };

    const handleSubmit = (e) => {
        e.preventDefault(); // Prevent default form submission
        onSave(formData);
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };



    const createNewUser = async () => {
        try {
            console.log('Creating new user for patient with data:', formData);
    
            const response = await fetch('https://service9.szapfs.org/APM-Create-User', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });
    
            if (!response.ok) {
                throw new Error('Failed to create new user');
            }
    
            const result = await response.json();
            console.log('New user created successfully:', result);
    
            // Directly fetch the updated list of users
            const usersResponse = await fetch(`https://service9.szapfs.org/appusers/patientid/${formData.patient_id}`);
            if (!usersResponse.ok) {
                throw new Error('Failed to fetch updated users list');
            }
            
            const usersData = await usersResponse.json();
            console.log('Updated app users received:', usersData);
            
            // Sort users and update state
            const sortedUsers = [...usersData].sort((a, b) => a.User_ID - b.User_ID);
            setAppUsers(sortedUsers);
            
            // Find and select the newest user (with highest User_ID)
            if (sortedUsers.length > 0) {
                const newestUser = sortedUsers.reduce((max, user) =>
                    user.User_ID > max.User_ID ? user : max, sortedUsers[0]
                );
                setSelectedUser(newestUser);
            }
        } catch (error) {
            console.error('Error creating new user:', error);
        }
    };
    
    const fetchAppUsers = async () => {
        console.log('Fetching app users for patient ID:', formData.patient_id);
        try {
            const response = await fetch(`https://service9.szapfs.org/appusers/patientid/${formData.patient_id}`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            console.log('App users data received:', data);

            // Sort users by User_ID (lowest first)
            const sortedUsers = [...data].sort((a, b) => a.User_ID - b.User_ID);
            setAppUsers(sortedUsers);

            // Select the first user by default (lowest User_ID)
            if (sortedUsers.length > 0) {
                setSelectedUser(sortedUsers[0]);
            }

            return data;
        } catch (err) {
            console.error('Error fetching app users:', err);
            return null;
        }
    };

    // Create modal content to render directly to document body
    const modalContent = (
        <div className={`PME-overlay ${isVisible ? 'pme-visible' : ''} ${isClosing ? 'pme-closing' : ''}`}>
            <div className={`PME-container ${isVisible ? 'pme-visible' : ''} ${isClosing ? 'pme-closing' : ''}`}>
                <div className="PME-modal">
                    <div className="PME-header">
                        <h2></h2>
                        <FontAwesomeIcon
                            icon={faTimes}
                            className="PME-close"
                            onClick={handleClose}
                        />
                    </div>

                    <form className="PME-form" onSubmit={handleSubmit}>
                        <div className="PME-form-grid">
                            <div className="PME-form-group">
                                <label>Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => handleChange('name', e.target.value)}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Family Number</label>
                                <input
                                    type="text"
                                    value={formData.family_number}
                                    onChange={(e) => handleChange('family_number', e.target.value)}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Date of Birth</label>
                                <input
                                    type="text"
                                    value={formData.date_of_birth}
                                    onChange={(e) => handleChange('date_of_birth', e.target.value)}
                                    placeholder="DD-MM-YYYY"
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Gender</label>
                                <select
                                    value={formData.gender}
                                    onChange={(e) => handleChange('gender', e.target.value)}
                                >
                                    <option value="">Select Gender</option>
                                    {GENDER_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="PME-form-group">
                                <label>Phone</label>
                                <input
                                    type="text"
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Height (cm)</label>
                                <input
                                    type="number"
                                    value={formData.height_cm}
                                    onChange={(e) => handleChange('height_cm', e.target.value)}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Weight (kg)</label>
                                <input
                                    type="number"
                                    value={formData.weight_kg}
                                    onChange={(e) => handleChange('weight_kg', e.target.value)}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Smoking (pcs/day)</label>
                                <input
                                    type="number"
                                    value={formData.smoking_pcs_per_day}
                                    onChange={(e) => handleChange('smoking_pcs_per_day', e.target.value)}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Alcohol Usage</label>
                                <select
                                    value={formData.alcohol_usage}
                                    onChange={(e) => handleChange('alcohol_usage', e.target.value)}
                                >
                                    <option value="">Select Usage</option>
                                    {ALCOHOL_OPTIONS.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="PME-form-group full-width">
                                <label>Chronic Disease</label>
                                <textarea
                                    value={formData.chronic_disease}
                                    onChange={(e) => handleChange('chronic_disease', e.target.value)}
                                    rows={2}
                                />
                            </div>

                            <div className="PME-form-group full-width">
                                <label>Drug Consumption</label>
                                <textarea
                                    value={formData.drug_consumption}
                                    onChange={(e) => handleChange('drug_consumption', e.target.value)}
                                    rows={2}
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Sample Acceptance Date</label>
                                <input
                                    type="text"
                                    value={formData.sample_acceptance_date}
                                    onChange={(e) => handleChange('sample_acceptance_date', e.target.value)}
                                    placeholder="DD-MM-YYYY"
                                />
                            </div>

                            <div className="PME-form-group">
                                <label>Sample Type</label>
                                <input
                                    type="text"
                                    value={formData.sample_type}
                                    onChange={(e) => handleChange('sample_type', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="PME-actions">
                            <button type="button" className="PME-button secondary" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="submit" className="PME-button primary">
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>

                <div className="APM-modal">
                    <div className="APM-header">
                    <div className="APM-user-circles">
  {appUsers.map(user => (
    <div
      key={user.User_ID}
      className={`APM-user-circle ${selectedUser && user.User_ID === selectedUser.User_ID ? 'selected' : ''}`}
      onClick={() => setSelectedUser(user)}
      title={`${user.First_Name || ''} ${user.Last_Name || ''}`}
    >
      {(user.First_Name && user.First_Name.charAt(0).toUpperCase()) || ''}
      {(user.Last_Name  && user.Last_Name.charAt(0).toUpperCase())  || ''}
    </div>
  ))}
</div>

                        <div className="APM-add-new-user-circle" onClick={createNewUser}>
                            <div className="APM-user-circle-icon"> <FontAwesomeIcon icon={faPlus} /></div>
                        </div>
                    </div>

                    <div className="APM-content">
                        <AppProfileModal appUser={selectedUser} />
                    </div>
                </div>

            </div>
            
        </div>
    );
    
    // Use createPortal to render modal directly to document body
    return createPortal(modalContent, document.body);
}

export default PatientMetadataEditor;
